import { useEffect, useState } from 'react';
import { X, Eye, EyeOff, Loader2 } from 'lucide-react';
import { authService, type PasswordPolicy } from '../../services/auth.service';
import PasswordPolicyHints, { validatePassword } from '../auth/PasswordPolicyHints';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { password: string; temporary: boolean }) => Promise<void>;
  userEmail: string;
}

/**
 * Admin action: set a new password for another user directly.
 *
 * If "Require user to change on next login" is checked, the password is pushed
 * to KC with `temporary=true` — the user is forced to change it the first time
 * they sign in.
 */
export default function SetPasswordModal({ isOpen, onClose, onSave, userEmail }: Props) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [temporary, setTemporary] = useState(true);
  const [show, setShow] = useState(false);
  const [policy, setPolicy] = useState<PasswordPolicy | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPassword('');
    setConfirm('');
    setTemporary(true);
    setError(null);
    authService.getPasswordPolicy().then(setPolicy).catch(() => setPolicy(null));
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    if (policy) {
      const policyError = validatePassword(policy, password, userEmail);
      if (policyError) {
        setError(`Password requirement not met: ${policyError}`);
        return;
      }
    } else if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setSaving(true);
    try {
      await onSave({ password, temporary });
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'Could not set the password — please try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">Set password</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Set a new password for <strong>{userEmail}</strong>.
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">New password</label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
                aria-label={show ? 'Hide password' : 'Show password'}
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <PasswordPolicyHints policy={policy} password={password} email={userEmail} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password</label>
            <input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoComplete="new-password"
              required
            />
          </div>

          <label className="flex items-start space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={temporary}
              onChange={(e) => setTemporary(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <span>
              Require the user to change this password on their next sign-in.
            </span>
          </label>

          {error && (
            <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm inline-flex items-center space-x-2 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{saving ? 'Saving…' : 'Set password'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
